// =============================================================================
// LoginPage — Authentication form for Portal
// =============================================================================

import { useState } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router';
import { useAuth } from '@/hooks/use-auth';
import { Button, Input, Card, Spinner } from '@tkpod/ui';

export function LoginPage() {
  const { login, error, clearError, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Where to redirect after login (default: dashboard, or pending join page)
  const pendingJoinId = sessionStorage.getItem('pendingJoinId');
  const from =
    (location.state as { from?: string } | null)?.from ??
    (pendingJoinId ? `/join/${pendingJoinId}` : '/dashboard');

  // Wait for auth initialization before deciding
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary">
        <Spinner size="lg" />
      </div>
    );
  }

  // Already authenticated — redirect away from login
  if (isAuthenticated) {
    sessionStorage.removeItem('pendingJoinId');
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSubmitting(true);

    try {
      await login({ email, password });
      sessionStorage.removeItem('pendingJoinId');
      navigate(from, { replace: true });
    } catch {
      // Error is captured in store — shown in UI below
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-600">
            <span className="text-lg font-bold text-white">TK</span>
          </div>
          <h1 className="text-2xl font-bold text-text-primary">Transfer Kingdom</h1>
          <p className="mt-1 text-sm text-text-secondary">Sign in to your POD portal</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4 p-6">
            {/* Error message */}
            {error && (
              <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
                {error}
              </div>
            )}

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              autoFocus
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />

            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-brand-600 transition-colors hover:text-brand-500"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={submitting || !email || !password}
            >
              {submitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </Card>

        <p className="mt-6 text-center text-sm text-text-secondary">
          Don&apos;t have an account?{' '}
          <Link
            to="/register"
            className="font-medium text-brand-600 hover:text-brand-500 transition-colors"
          >
            Create account
          </Link>
        </p>

        <p className="mt-4 text-center text-xs text-text-tertiary">
          &copy; {new Date().getFullYear()} Transfer Kingdom. All rights reserved.
        </p>
      </div>
    </div>
  );
}
